/**
 * Copyright Highway9 Networks Inc.
 */
import { useMemo } from "react";
import { useAbility } from "@casl/react";
import { useAppSelector } from "~/store/hooks";
import { selectUserPermssionsLoading } from "~/store/context/authSlice";
import { AbilityContext } from "~/rbac/AbilityContext";
import { AppSubject, CrudAction } from "~/types/rbac";
import type { AppAbility, AppAction } from "~/types/rbac";

export function useAppAbility(): AppAbility {
  return useAbility(AbilityContext);
}

/**
 * Check a single permission. Prefer {@link useSubjectPermissions} when you need several CRUD flags for one object.
 */
export function useCan(action: AppAction, subject: AppSubject): boolean {
  const ability = useAppAbility();
  return ability.can(action, subject);
}

export type SubjectPermissions = {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

function buildSubjectPermissions(ability: AppAbility, subject: AppSubject): SubjectPermissions {
  return {
    canView: ability.can(CrudAction.view, subject),
    canAdd: ability.can(CrudAction.add, subject),
    canEdit: ability.can(CrudAction.edit, subject),
    canDelete: ability.can(CrudAction.delete, subject),
  };
}

/**
 * CRUD permission flags for one backend objectType (e.g. "radio", "radioSite").
 */
export function useSubjectPermissions(subject: AppSubject): SubjectPermissions {
  const ability = useAppAbility();
  return useMemo(() => buildSubjectPermissions(ability, subject), [ability, subject]);
}

/**
 * CRUD flags for multiple subjects in one memoized object (e.g. radio toolbar).
 */
/** Stable subject map — define at module scope when using {@link useSubjectsPermissions}. */
export function useSubjectsPermissions<const T extends Record<string, AppSubject>>(
  subjects: T
): { [K in keyof T]: SubjectPermissions } {
  const ability = useAppAbility();
  const subjectList = Object.values(subjects).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: subjects map should be module-level constant; subjectList is a stable string proxy for its content
  return useMemo(() => {
    const result = {} as { [K in keyof T]: SubjectPermissions };
    for (const key of Object.keys(subjects) as (keyof T)[]) {
      result[key] = buildSubjectPermissions(ability, subjects[key]);
    }
    return result;
  }, [ability, subjectList]);
}

/** Imperative check outside React (e.g. route guards); pass ability from store snapshot if needed. */
export function checkPermission(
  ability: AppAbility,
  crud: AppAction,
  subject: AppSubject
): boolean {
  return ability.can(crud, subject);
}

/** Whether user permissions are still being fetched (onAppStart). */
export function usePermissionsLoading(): boolean {
  return useAppSelector(selectUserPermssionsLoading);
}
